import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import axiosInstance from "../../utils/axios";

export enum SubscriptionStatus {
  TRIALING = "trialing",
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  SUSPENDED = "suspended",
  EXPIRED = "expired",
}

export interface Subscription {
  plan: any;
  id: number;
  status: SubscriptionStatus;
  trialStart?: string | null;
  trialEnd?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  createdAt: string;
  updatedAt: string;
  associationId: number;
  planId: number;
}

export interface ManagedSubscription extends Subscription {
  association?: { id: number; name: string };
  outstanding?: { count: number; sumCents: number };
}

interface SubscriptionState {
  subscriptions: Subscription[];
  subscription?: Subscription;
  allSubscriptions: ManagedSubscription[];
  allSubscriptionsStatus: "idle" | "loading" | "failed";
  status: "idle" | "loading" | "failed";
}

const initialState: SubscriptionState = {
  subscriptions: [],
  subscription: undefined,
  allSubscriptions: [],
  allSubscriptionsStatus: "idle",
  status: "idle",
};

export const fetchSubscriptionsByAssociation = createAsyncThunk(
  "subscription/fetchSubscriptionsByAssociation",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/subscription/association/${associationId}`
    );
    return response.data;
  }
);

export const fetchSubscriptionById = createAsyncThunk(
  "subscription/fetchSubscriptionById",
  async (id: number) => {
    const response = await axiosInstance.get(`/subscription/${id}`);
    return response.data;
  }
);

export const fetchAllSubscriptions = createAsyncThunk(
  "subscription/fetchAllSubscriptions",
  async () => {
    const response = await axiosInstance.get("/subscriptions");
    return response.data;
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSubscriptions(state, action) {
      state.subscriptions = action.payload;
    },
    setSubscription(state, action) {
      state.subscription = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionsByAssociation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSubscriptionsByAssociation.fulfilled, (state, action) => {
        state.status = "idle";
        state.subscriptions = action.payload;
      })
      .addCase(fetchSubscriptionsByAssociation.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchSubscriptionById.fulfilled, (state, action) => {
        state.subscription = action.payload;
      })
      .addCase(fetchAllSubscriptions.pending, (state) => {
        state.allSubscriptionsStatus = "loading";
      })
      .addCase(fetchAllSubscriptions.fulfilled, (state, action) => {
        state.allSubscriptionsStatus = "idle";
        state.allSubscriptions = action.payload;
      })
      .addCase(fetchAllSubscriptions.rejected, (state) => {
        state.allSubscriptionsStatus = "failed";
      });
  },
});

export function createSubscription(values: Partial<Subscription>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/subscription`, values);
    dispatch(fetchSubscriptionsByAssociation(values.associationId!));
    return response.data;
  };
}

export function updateSubscription(id: number, values: Partial<Subscription>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.put(`/subscription/${id}`, values);
    dispatch(fetchSubscriptionsByAssociation(values.associationId!));
    return response.data;
  };
}

export function deleteSubscription(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/subscription/${id}`);
    dispatch(fetchSubscriptionsByAssociation(associationId));
  };
}

/**
 * Cancel a subscription (superadmin). Sets status to CANCELED and, by default,
 * voids the association's outstanding invoices. Irreversible via this action —
 * the caller confirms first.
 */
export function cancelSubscription(id: number, voidOutstanding = true) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/subscription/${id}/cancel`, {
      voidOutstanding,
    });
    dispatch(fetchAllSubscriptions());
    return response.data;
  };
}

export const { setSubscriptions, setSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
